import React, { useContext, useEffect } from "react";
import { LanguageContext } from "../Context/LanguageContext";
import AOS from "aos";
import "aos/dist/aos.css";

function About() {
  const { texts } = useContext(LanguageContext);

  useEffect(() => {
    AOS.init({
      duration: 1000, // animation duration in ms
      once: true, // whether animation should happen only once
      easing: "ease-in-out",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-neutral-50 to-primary-50/20">
      {/* Enhanced Hero Section */}
      <div className="relative h-[80vh] md:h-screen about-bg-img bg-cover bg-center overflow-hidden">
        {/* Multi-layered Enhanced overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-primary-900/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        
        {/* Animated patterns */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-64 h-64 bg-primary-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl animate-pulse animation-delay-1000"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Enhanced Hero Content */}
          <div
            className="flex flex-col items-center text-center py-12 md:py-16"
            data-aos="zoom-in"
            data-aos-duration="1000"
          >
            {/* Main card with enhanced glassmorphism */}
            <div className="relative group max-w-5xl w-full">
              {/* Main content card */}
              <div className="relative bg-gradient-to-br from-black/50 to-black/40 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 border border-white/30 shadow-2xl">
                {/* Decorative corner accents */}
                <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-lg"></div>
                <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-lg"></div>
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-lg"></div>
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-lg"></div>
                
                {/* Badge/Label */}
                <div 
                  className="inline-flex items-center gap-2 bg-primary-400/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-primary-400/30 mb-4"
                  data-aos="fade-down"
                  data-aos-duration="600"
                >
                  <span className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-pulse"></span>
                  <span className="text-white/90 text-xs font-semibold tracking-wide uppercase">  {texts.since_2009}</span>
                </div>
                
                <h1 
                  className="text-white font-extrabold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 md:mb-5 leading-tight tracking-tight"
                  data-aos="zoom-in"
                  data-aos-offset="200"
                  data-aos-easing="ease-out-cubic"
                  data-aos-duration="800"
                >
                  <span className=" bg-gradient-to-r from-white via-white to-primary-100 bg-clip-text text-transparent drop-shadow-lg">
                    {texts.about_us}
                  </span>
                </h1>
                
                <p 
                  className="text-white/95 font-medium text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-6 md:mb-7 max-w-2xl mx-auto leading-relaxed"
                  data-aos='fade-up'
                  data-aos-delay="200"
                  data-aos-duration="800"
                >
                  {texts.about_section_text}
                </p>

                {/* Stats row */}
                <div 
                  className="grid grid-cols-3 gap-2 sm:gap-4 mt-5 sm:mt-7 pt-5 sm:pt-7 border-t border-white/20"
                  data-aos="fade-up"
                  data-aos-delay="600"
                >
                  <div className="text-center">
                    <div className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-0.5">15+</div>
                    <div className="text-white/70 text-xs font-medium">Years</div>
                  </div>
                  <div className="text-center border-x border-white/20">
                    <div className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-0.5">100K+</div>
                    <div className="text-white/70 text-xs font-medium">Customers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-0.5">10+</div>
                    <div className="text-white/70 text-xs font-medium">Locations</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" data-aos="fade-up" data-aos-delay="1000">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/70 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {/* Our Company Section - Enhanced */}
            <div
              className="relative group"
              data-aos="fade-up"
            >
              {/* Glowing effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-400 to-primary-600 rounded-3xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
              
              <div className="relative bg-white rounded-3xl shadow-xl p-8 md:p-12 lg:p-16 border border-neutral-100 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-bl-full opacity-50"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-primary-50 rounded-tr-full opacity-30"></div>
                
                <div className="relative">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h2 className="text-neutral-900 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3 bg-gradient-to-r from-neutral-900 to-primary-900 bg-clip-text text-transparent">
                      {texts.our_company}
                    </h2>
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                        <div className="w-16 h-0.5 bg-gradient-to-r from-primary-400 to-transparent"></div>
                      </div>
                      <div className="w-3 h-3 bg-primary-400 rounded-full animate-pulse shadow-lg shadow-primary-400/50"></div>
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-0.5 bg-gradient-to-l from-primary-400 to-transparent"></div>
                        <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed text-center max-w-5xl mx-auto font-medium">
                    {texts.our_company_subtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Our Targets Section - Enhanced */}
            <div
              className="relative group"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              {/* Glowing effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-400 to-primary-600 rounded-3xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
              
              <div className="relative bg-gradient-to-br from-primary-50 via-white to-primary-50/50 rounded-3xl shadow-xl p-8 md:p-12 lg:p-16 border border-primary-100 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 left-0 w-40 h-40 bg-primary-100 rounded-br-full opacity-50"></div>
                
                <div className="relative">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h2 className="text-neutral-900 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3 bg-gradient-to-r from-neutral-900 to-primary-900 bg-clip-text text-transparent">
                      {texts.our_targets}
                    </h2>
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                        <div className="w-16 h-0.5 bg-gradient-to-r from-primary-400 to-transparent"></div>
                      </div>
                      <div className="w-3 h-3 bg-primary-400 rounded-full animate-pulse shadow-lg shadow-primary-400/50"></div>
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-0.5 bg-gradient-to-l from-primary-400 to-transparent"></div>
                        <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed text-center max-w-5xl mx-auto font-medium">
                    {texts.our_targets_subtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Our Journey Section - Enhanced */}
            <div
              className="relative group"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              {/* Glowing effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-400 to-primary-600 rounded-3xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
              
              <div className="relative bg-white rounded-3xl shadow-xl p-8 md:p-12 lg:p-16 border border-neutral-100 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute bottom-0 right-0 w-48 h-48 bg-primary-50 rounded-tl-full opacity-40"></div>
                
                <div className="relative">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h2 className="text-neutral-900 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3 bg-gradient-to-r from-neutral-900 to-primary-900 bg-clip-text text-transparent">
                      {texts.our_journey}
                    </h2>
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                        <div className="w-16 h-0.5 bg-gradient-to-r from-primary-400 to-transparent"></div>
                      </div>
                      <div className="w-3 h-3 bg-primary-400 rounded-full animate-pulse shadow-lg shadow-primary-400/50"></div>
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-0.5 bg-gradient-to-l from-primary-400 to-transparent"></div>
                        <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed text-center max-w-5xl mx-auto font-medium">
                    {texts.our_journey_subtitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="relative mt-20">
          <div
            className="absolute top-0 left-1/6 w-12 h-12 bg-primary-100 rounded-full opacity-40 hidden lg:block"
            data-aos="fade-right"
          ></div>
          <div
            className="absolute -bottom-8 right-1/4 w-16 h-16 bg-primary-200 rounded-full opacity-30 hidden lg:block"
            data-aos="fade-left"
          ></div>
          <div
            className="absolute top-12 right-1/6 w-8 h-8 bg-primary-300 rounded-full opacity-50 hidden lg:block"
            data-aos="fade-down"
          ></div>
        </div>
      </div>
    </div>
  );
}

export default About;
